import { useState } from "react";
import type { Project } from "../services/api";
import { listProjects } from "../services/api";
import { getErrorAlertMessage } from "../utils/errors";
import { PROJECT_STATUSES } from "../constants/projectStatus";

const EXPORT_FIELDS: Array<keyof Project> = [
  "project_id",
  "title",
  "status",
  "start_date",
  "end_date",
  "week_start",
  "delivery_unit",
  "aws_role",
  "aws_grade",
  "tw_role",
  "tw_grade",
  "tw_archetype",
  "fte_allocation",
  "staffing_status",
  "submitted_to_aws",
  "outcome",
  "estimated_skills",
  "proposed_candidates",
  "notes",
  "requested_hours",
  "avg_week_hours",
  "created_at",
  "updated_at",
];

function toCell(value: unknown): string {
  if (Array.isArray(value)) {
    return value.join("; ");
  }
  if (value === null || value === undefined) {
    return "";
  }
  return String(value);
}

function toCsv(projects: Project[]): string {
  const headerRow = EXPORT_FIELDS.join(",");
  const dataRows = projects.map((project) =>
    EXPORT_FIELDS.map((field) => {
      const raw = project[field];
      const cell = toCell(raw);
      const escaped = cell.replace(/"/g, '""');
      return `"${escaped}"`;
    }).join(","),
  );
  return [headerRow, ...dataRows].join("\n");
}

export function ExportRRs() {
  const [isExporting, setIsExporting] = useState(false);
  const [alert, setAlert] = useState<{ type: "info" | "error"; message: string } | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [allActive, setAllActive] = useState(false);

  function handleStatusChange(options: string[]) {
    setSelectedStatuses(options);
  }

  async function handleExport() {
    setIsExporting(true);
    setAlert(null);
    try {
      const projects = await listProjects();
      const effectiveStatuses =
        allActive && selectedStatuses.length === 0
          ? PROJECT_STATUSES.filter((status) => status !== "EXPIRED" && status !== "ACCEPTED")
          : selectedStatuses;
      const filtered =
        effectiveStatuses.length === 0
          ? projects
          : projects.filter((project) => effectiveStatuses.includes((project.status ?? "").toUpperCase()));
      if (!projects || projects.length === 0) {
        setAlert({ type: "info", message: "There are no requisitions to export right now." });
        return;
      }
      if (filtered.length === 0) {
        setAlert({ type: "info", message: "No requisitions match the selected status filters." });
        return;
      }
      const csv = toCsv(filtered);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "requisitions-export.csv";
      link.click();
      URL.revokeObjectURL(url);
      setAlert({ type: "info", message: `Exported ${filtered.length} requisitions.` });
    } catch (err) {
      setAlert({ type: "error", message: getErrorAlertMessage("export requisitions", err) });
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <section className="panel">
      <h2>Export RR Data</h2>
      <p className="helper-text">Download all requisition fields as CSV for analysis or backup.</p>
      <div className="form-grid" style={{ marginBottom: "0.75rem" }}>
        <label className="checkbox-field" style={{ alignItems: "center", justifyContent: "space-between" }}>
          <span>All active (excludes EXPIRED, ACCEPTED)</span>
          <input
            type="checkbox"
            checked={allActive}
            onChange={(event) => {
              const checked = event.target.checked;
              setAllActive(checked);
              if (checked) {
                setSelectedStatuses([]);
              }
            }}
          />
        </label>
      </div>
      <label style={{ display: "block", marginBottom: "0.75rem" }}>
        Status Filter (multi-select)
        <select
          multiple
          value={selectedStatuses}
          onChange={(event) => {
            const options = Array.from(event.target.selectedOptions).map((opt) => opt.value);
            handleStatusChange(options);
            if (options.length > 0 && allActive) {
              setAllActive(false);
            }
          }}
          style={{ width: "100%", minHeight: "8rem" }}
        >
          {PROJECT_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
        <small className="helper-text">Leave empty to export all statuses.</small>
      </label>
      <button type="button" className="primary" onClick={() => void handleExport()} disabled={isExporting}>
        {isExporting ? "Exporting…" : "Export RRs"}
      </button>
      {alert && <div className={`alert ${alert.type === "error" ? "error" : "info"}`}>{alert.message}</div>}
    </section>
  );
}
